import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AutoComplete, Input } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getAssetSelected, updatedAsset, simulationAssetSell } from "../../../../store/actions/precog/assets";
import LoaderSimple from "../../../general/loader";
import moment from "moment";
import numeral from "numeral";
import { VaporDatePicker } from "../../../vapor";
import { Tooltip } from "antd";
import NumberFormat from "react-number-format";
import { Toggle } from "@ts-digital/vrc";

/**
 * Drawer to interact with a fixed asset searched and selected using
 * SearchAssetsDrawerSection
 * @param {*} param0
 * @returns
 */
const SelectedAssetsDrawerSection = ({
  businessId,
  isSellingInvoice,
  assetsDetails,
  setAssetSearched,
  setDisableSaveButton,
  setIsAssetSelected,
  activeSaveInterval,
  setIsDrawerAccountsDetail,
  setFieldsToDelete,
}) => {
  const [state, setState] = useState();
  const [dispatchDone, setDispatchDone] = useState(false);
  const dispatch = useDispatch();
  const dateFormatList = ["DD/MM/YYYY", "DD/MM/YY", "DD-MM-YYYY", "DD-MM-YY", "DDMMYYYY", "DDMMYY"];

  useEffect(() => {
    if (!assetsDetails) return;
    dispatch(getAssetSelected(assetsDetails))
      .then((res) => {
        const newState = { ...res };
        newState.operationDate = moment(newState.operationDate);
        newState.residualReferenceDate = moment(newState.residualReferenceDate);
        setState(newState);
        setDispatchDone(true);
        dispatchSimulation(newState);
      })
      .catch((err) => {});
  }, [assetsDetails]);

  const dispatchSimulation = (newState = null) => {
    const simState = newState ? newState : state;
    dispatch(
      simulationAssetSell(simState.assetRegistryId, {
        disposalRate:
          typeof simState.sellingPercent === "string"
            ? parseFloat(simState.sellingPercent.substring(0, simState.sellingPercent.length - 1))
            : simState.sellingPercent,
        saleAmount: simState.amount,
        date: moment(simState.operationDate).format("YYYY-MM-DD"),
        businessId: businessId,
      })
    ).then((res) => {
      setState((prevState) => ({
        ...prevState,
        depreciationDuringYear: res.depreciation,
        statutoryCapitalGain: res.statutoryCapitalGain,
        statutoryCapitalLoss: res.statutoryCapitalLoss,
        taxCapitalGain: res.taxCapitalGain,
        taxCapitalLoss: res.taxCapitalLoss,
      }));
      activeSaveInterval();
    });
  };

  /**
   * Function to do patch with updated value in the asset
   * @param {*} field
   */
  const autosave = (field, newData = null) => {
    let data = null;
    switch (field) {
      case "operationDescription":
        data = { operationDescription: state.operationDescription };
        break;
      case "operationDate":
        data = { operationDate: moment(state.operationDate).format("YYYY-MM-DD") };
        break;
      case "sellingPercent":
        data = newData
          ? {
              sellingPercent:
                typeof state.sellingPercent === "string"
                  ? parseFloat(state.sellingPercent.substring(0, state.sellingPercent.length - 1))
                  : state.sellingPercent,
            }
          : { sellingPercent: newData };
        break;
      case "distributeCapitalGains":
        data = { distributeCapitalGains: newData };
        break;
      default:
        break;
    }

    if (data) {
      dispatch(updatedAsset(assetsDetails, data)).then((res) => {
        activeSaveInterval();
      });
    }
    if (field !== "sellingPercent" || field !== "operationDate") dispatchSimulation();
  };

  /**
   * Render the drawer fields in case of selling invoice
   */
  const renderSellingInvoice = () => {
    return (
      <>
        <div className="drawer-search-body-item" style={{ width: "235px" }}>
          <div className="drawer-search-text">
            <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.sellingPercentage"} />
          </div>
          <NumberFormat
            displayType={"number"}
            allowEmptyFormatting={true}
            allowLeadingZeros={true}
            thousandSeparator={"."}
            decimalSeparator={","}
            decimalScale={2}
            className="inputCustom drawer-inputCustom"
            placeholder="-"
            suffix={!!state.sellingPercent && state.sellingPercent > 0 ? "%" : ""}
            isNumericString={!!state.sellingPercent && state.sellingPercent > 0 ? true : false}
            value={!!state.sellingPercent && state.sellingPercent > 0 ? numeral(state.sellingPercent).format("0,0.00") : ""}
            onValueChange={(el) => {
              setState({ ...state, sellingPercent: el.floatValue > 100 ? 100 : el.floatValue });
            }}
            onBlur={(newData) => {
              autosave("sellingPercent", newData);
            }}
          />
        </div>
        <div className="drawer-search-body-item" style={{ width: "150px" }}>
          <div className="drawer-search-text">
            <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.sellingValue"} />
          </div>
          <Input
            className="inputCustom drawer-inputCustom readonly"
            readOnly={true}
            value={(state.amount ? numeral(Math.abs(state.amount)).format("0,0.00") : 0) + "€"}
          />
        </div>
        <div className="drawer-search-body-item" style={{ width: "200px" }}>
          <div className="drawer-search-text">
            <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.ammortizationDuringYear"} />
          </div>
          <Input
            className="inputCustom drawer-inputCustom readonly"
            value={(state.depreciationDuringYear ? numeral(Math.abs(state.depreciationDuringYear)).format("0,0.00") : 0) + "€"}
            readOnly={true}
          />
        </div>
        {state.statutoryCapitalLoss && (
          <div className="drawer-search-body-item" style={{ width: "35%" }}>
            <div className="drawer-search-text">
              <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.civilisticCapitalLoss"} />
            </div>
            <Input
              className="inputCustom drawer-inputCustom readonly"
              value={(state.statutoryCapitalLoss ? numeral(Math.abs(state.statutoryCapitalLoss)).format("0,0.00") : 0) + "€"}
              readOnly={true}
            />
          </div>
        )}
        {state.statutoryCapitalGain && (
          <div className="drawer-search-body-item" style={{ width: "35%" }}>
            <div className="drawer-search-text">
              <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.civilisticCapitalGain"} />
            </div>
            <Input
              className="inputCustom drawer-inputCustom readonly"
              value={(state.statutoryCapitalGain ? numeral(Math.abs(state.statutoryCapitalGain)).format("0,0.00") : 0) + "€"}
              readOnly={true}
            />
          </div>
        )}
        {state.taxCapitalLoss && (
          <div className="drawer-search-body-item" style={{ width: "35%" }}>
            <div className="drawer-search-text">
              <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.fiscalCapitalLoss"} />
            </div>
            <Input
              className="inputCustom drawer-inputCustom readonly"
              value={(state.taxCapitalLoss ? numeral(Math.abs(state.taxCapitalLoss)).format("0,0.00") : 0) + "€"}
              readOnly={true}
            />
          </div>
        )}
        {state.taxCapitalGain && (
          <div className="drawer-search-body-item" style={{ width: "35%" }}>
            <div className="drawer-search-text">
              <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.fiscalCapitalGain"} />
            </div>
            <Input
              className="inputCustom drawer-inputCustom readonly"
              value={(state.taxCapitalGain ? numeral(Math.abs(state.taxCapitalGain)).format("0,0.00") : 0) + "€"}
              readOnly={true}
            />
          </div>
        )}
        {state.taxCapitalGain && (
          <div style={{ display: "flex", marginTop: "40px", marginLeft: "5px" }}>
            <div className="precog-withholding-drawer-form—label-text">
              <FormattedMessage id={"preCog.assetsTab.drawer.distributeCapitalGains"} />
            </div>
            <Toggle
              style={{ marginTop: "2px", marginLeft: "10px" }}
              onChange={(value) => {
                setState({ ...state, distributeCapitalGains: value === undefined ? false : value });
                autosave("distributeCapitalGains", value === undefined ? false : value);
              }}
              checked={state["distributeCapitalGains"] || false}
            />
          </div>
        )}
      </>
    );
  };

  /**
   * Render the drawer fields in case of purchase invoice
   */
  const renderPurchaseInvoice = () => {
    return (
      <>
        <div className="drawer-search-body-item" style={{ width: "150px" }}>
          <div className="drawer-search-text">
            <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.value"} />
          </div>
          <Input
            className="inputCustom drawer-inputCustom readonly"
            value={(state.amount ? numeral(Math.abs(state.amount)).format("0,0.00") : 0) + "€"}
            onChange={(e) => setState({ ...state, amount: e.target.value })}
            readOnly={true}
          />
        </div>
      </>
    );
  };

  if (!dispatchDone) return <LoaderSimple message="Caricamento dati in corso. Si prega di attendere." />;

  /**
   * In return render all common components between purchase and selling invoice
   */
  return (
    <div className="search-assets-drawer-section">
      <div className="drawer-search-header flex">
        <div
          className="drawer-search-header-col"
          style={{
            width: "455px",
            borderRight: "1px rgb(201, 217, 232) solid",
            marginRight: "22px",
          }}
        >
          <div className="drawer-search-text">
            <FormattedMessage id={"preCog.assetsTab.drawer.existingAsset"} />
          </div>
          <Tooltip color={"rgb(0, 80, 117)"} title={state.assetDescription}>
            <AutoComplete className="drawer-search-autocomplete" value={state.assetDescription || ""} style={{ width: "275px" }}>
              <Input.Search
                size="large"
                className="drawer-search-input"
                enterButton={<FontAwesomeIcon icon={["far", "times"]} />}
                onSearch={() => {
                  setDisableSaveButton(false);
                  setAssetSearched(null);
                  setIsAssetSelected(false);
                }}
              />
            </AutoComplete>
          </Tooltip>
          <div className="drawer-search-subtext">
            <span style={{ cursor: "pointer" }} className="blue" onClick={() => setIsDrawerAccountsDetail(true)}>
              Conto {state.accountCode}
            </span>
            {" - "}
            <span>Data Attivazione {moment(state.activationDate).format("DD/MM/YYYY")}</span>
          </div>
        </div>
        <div
          className="drawer-search-header-col"
          style={{
            width: "365px",
          }}
        >
          <div className="drawer-search-header-overtext">{numeral(state.assetAmount).format("0,0.00")} €</div>
          <div className="drawer-search-subtext under-text">Valore cespite esistente</div>
          <div className="drawer-search-header-overtext bold">{numeral(state.residualAmount).format("0,0.00")} €</div>
          <div className="drawer-search-subtext under-text">Residuo al {moment(state.residualReferenceDate).format("DD/MM/YYYY")}</div>
        </div>
      </div>
      <div className="drawer-search-body">
        <div className="drawer-search-filter-title">
          <FormattedMessage id={"preCog.assetsTab.drawer.newOperation"} />
        </div>
        <div className="drawer-search-body-container">
          <div className="drawer-search-body-item" style={{ width: "360px" }}>
            <div className="drawer-search-text">
              <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.type"} />
            </div>
            <Input
              className="inputCustom drawer-inputCustom readonly"
              readOnly={true}
              value={
                state.operationType ? useIntl().formatMessage({ id: `preCog.assetsTab.drawer.selectedExisting.${state.operationType.toLowerCase()}` }) : ""
              }
            />
          </div>
          <div className="drawer-search-body-item" style={{ width: "150px" }}>
            <div className="drawer-search-text">
              <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.operationDate"} />
            </div>
            <VaporDatePicker
              className="inputCustom drawer-inputCustom"
              value={state.operationDate ? moment(state.operationDate) : null}
              onChange={(value) => {
                setState({ ...state, operationDate: value === null ? null : moment(`${value}`).toDate() });
              }}
              format={dateFormatList}
              onBlur={() => autosave("operationDate")}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </div>
          {isSellingInvoice ? renderSellingInvoice() : renderPurchaseInvoice()}
        </div>
      </div>
      <div className="drawer-search-footer">
        <div className="drawer-search-text">
          <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.description"} />
        </div>
        <Input.TextArea
          maxLength={200}
          className="inputCustom drawer-textArea"
          allowClear
          value={state.operationDescription || ""}
          onChange={(e) => setState({ ...state, operationDescription: e.target.value })}
          onBlur={() => {
            if (state.operationDescription) {
              autosave("operationDescription");
            } else {
              setFieldsToDelete(["operationDescription"]);
            }
          }}
        />
        <div className="drawer-search-show-movements">
          <FormattedMessage id={"preCog.assetsTab.drawer.selectedExisting.showMovements"} />
        </div>
      </div>
    </div>
  );
};

export default SelectedAssetsDrawerSection;
